import React, { useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MEDICAL_EVENT_LABELS, MEDICAL_DUE_SOON_DAYS,
  useShelterReminderPage, useShelterRemindersInRange, useShelterReminderCounts,
  useShelterAnimalOptions, useLoadMoreSentinel,
  buildMonthGrid, buildWeekGrid, gridRange, addMonths, addDays,
  formatMonthTitle, formatWeekTitle, startOfWeek,
} from '@rehome/shared';
import type { MedicalEventType, AnimalMedicalReminder } from '@rehome/shared';
import {
  CalendarClock, AlertTriangle, Stethoscope, List, CalendarDays,
  ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import { SearchableAnimalSelect } from '../components/SearchableAnimalSelect';
import { ReminderDialog } from '../components/ReminderDialog';
import { ReminderCalendar, type CalendarMode } from '../components/ReminderCalendar';

// ─── MedicalRemindersPage ───────────────────────────────────────────────────
// A menhely összes ismétlődő egészségügyi eseménye (oltás, féreghajtás, …)
// két nézetben: `Lista` esedékesség szerint, és `Naptár` hónap/hét bontásban.
//
// A két nézet MÁS lekérdezést használ: a lista kurzorosan lapoz, a naptár
// csak a látható rács napjait kéri le. Az indoklás: shared/src/reminders.ts.
// ===========================================================================

type Tab = 'list' | 'calendar';

const daysUntil = (dateStr: string): number =>
  Math.round((new Date(dateStr).getTime() - new Date().setHours(0, 0, 0, 0)) / 86_400_000);

const MedicalRemindersPage: React.FC = () => {
  const { shelterId } = useParams<{ shelterId: string }>();
  const navigate = useNavigate();

  const [tab, setTab] = useState<Tab>('list');
  const [calendarMode, setCalendarMode] = useState<CalendarMode>('month');
  const [viewDate, setViewDate] = useState(() => new Date());

  const [animalFilter, setAnimalFilter] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<MedicalEventType | null>(null);
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [selected, setSelected] = useState<AnimalMedicalReminder | null>(null);

  const filters = useMemo(
    () => ({ animalId: animalFilter, eventType: typeFilter, onlyOverdue }),
    [animalFilter, typeFilter, onlyOverdue]
  );

  const { options: animalOptions } = useShelterAnimalOptions(shelterId);
  const { counts, refetch: refetchCounts } = useShelterReminderCounts(shelterId);

  // ─── Lista ───────────────────────────────────────────────────────────────
  const list = useShelterReminderPage(shelterId, filters);
  const sentinelRef = useLoadMoreSentinel(list.loadMore, list.hasMore && !list.loading);

  // ─── Naptár ──────────────────────────────────────────────────────────────
  // A tartomány a LÁTHATÓ rácsé — a hónapnézet a kilógó napokat is mutatja.
  const range = useMemo(() => {
    const grid = calendarMode === 'month' ? buildMonthGrid(viewDate) : buildWeekGrid(viewDate);
    return gridRange(grid);
  }, [calendarMode, viewDate]);

  const calendar = useShelterRemindersInRange(shelterId, range.from, range.to, filters);

  const handleChanged = useCallback(() => {
    list.reload();
    calendar.reload();
    refetchCounts();
  }, [list, calendar, refetchCounts]);

  const stepView = (direction: -1 | 1) => {
    setViewDate(prev => calendarMode === 'month'
      ? addMonths(prev, direction)
      : addDays(startOfWeek(prev), direction * 7));
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <CalendarClock className="w-8 h-8 text-teal-600" />
            Emlékeztetők
          </h1>
          <p className="text-gray-600 mt-1">
            Az összes ismétlődő egészségügyi esemény (oltás, féreghajtás, stb.) esedékesség szerint
          </p>
        </div>

        {/* ── Összegzés ────────────────────────────────────────────────────
            Korábban három nagy kártya foglalta el az oldal tetejét. A számok
            fontosak, de nem ők a lényeg — a beosztás az. Ezért egy visszafogott
            csík; a "Lejárt" továbbra is kattintható szűrő. */}
        <div className="flex items-center gap-1 text-sm bg-white border border-gray-200 rounded-lg px-1 py-1 shadow-sm self-start lg:self-auto">
          <SummaryChip
            icon={<Stethoscope className="w-3.5 h-3.5" />}
            label="Összes"
            value={counts?.total}
            tone="neutral"
          />
          <span className="w-px h-6 bg-gray-200" />
          <SummaryChip
            icon={<CalendarClock className="w-3.5 h-3.5" />}
            label={`${MEDICAL_DUE_SOON_DAYS} napon belül`}
            value={counts?.dueSoon}
            tone="amber"
          />
          <span className="w-px h-6 bg-gray-200" />
          <SummaryChip
            icon={<AlertTriangle className="w-3.5 h-3.5" />}
            label="Lejárt"
            value={counts?.overdue}
            tone="red"
            active={onlyOverdue}
            onClick={() => setOnlyOverdue(v => !v)}
            title={onlyOverdue ? 'Szűrés törlése' : 'Csak a lejártak'}
          />
        </div>
      </div>

      {/* ── Tabok és szűrők ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
          <div className="inline-flex bg-gray-100 rounded-lg p-1 self-start" role="tablist">
            <TabButton active={tab === 'list'} onClick={() => setTab('list')} icon={<List className="w-4 h-4" />}>
              Lista
            </TabButton>
            <TabButton active={tab === 'calendar'} onClick={() => setTab('calendar')} icon={<CalendarDays className="w-4 h-4" />}>
              Naptár
            </TabButton>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <SearchableAnimalSelect
              options={animalOptions}
              value={animalFilter}
              onChange={setAnimalFilter}
              className="sm:w-56"
            />
            <select
              value={typeFilter ?? 'All'}
              onChange={e => setTypeFilter(e.target.value === 'All' ? null : e.target.value as MedicalEventType)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
            >
              <option value="All">Minden típus</option>
              {(Object.keys(MEDICAL_EVENT_LABELS) as MedicalEventType[]).map(t => (
                <option key={t} value={t}>{MEDICAL_EVENT_LABELS[t]}</option>
              ))}
            </select>
          </div>
        </div>

        {tab === 'calendar' && (
          <div className="px-4 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button" onClick={() => stepView(-1)}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                aria-label={calendarMode === 'month' ? 'Előző hónap' : 'Előző hét'}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <h2 className="text-base font-bold text-gray-800 min-w-[13rem] text-center">
                {calendarMode === 'month' ? formatMonthTitle(viewDate) : formatWeekTitle(viewDate)}
              </h2>
              <button
                type="button" onClick={() => stepView(1)}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
                aria-label={calendarMode === 'month' ? 'Következő hónap' : 'Következő hét'}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button" onClick={() => setViewDate(new Date())}
                className="ml-1 text-sm font-medium px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Ma
              </button>
            </div>

            <div className="inline-flex bg-gray-100 rounded-lg p-1">
              <TabButton active={calendarMode === 'month'} onClick={() => setCalendarMode('month')}>Hónap</TabButton>
              <TabButton active={calendarMode === 'week'} onClick={() => setCalendarMode('week')}>Hét</TabButton>
            </div>
          </div>
        )}

        <div className="p-4">
          {tab === 'calendar' ? (
            <ReminderCalendar
              mode={calendarMode}
              viewDate={viewDate}
              reminders={calendar.reminders}
              loading={calendar.loading}
              truncated={calendar.truncated}
              onSelect={setSelected}
            />
          ) : (
            <ListView
              state={list}
              sentinelRef={sentinelRef}
              onSelect={setSelected}
              onOpenAnimal={id => navigate(`/shelter/${shelterId}/animals/${id}/medical`)}
            />
          )}
        </div>
      </div>

      <ReminderDialog
        reminder={selected}
        shelterId={shelterId}
        onClose={() => setSelected(null)}
        onChanged={handleChanged}
      />
    </div>
  );
};

// ─── Lista nézet ────────────────────────────────────────────────────────────

const ListView: React.FC<{
  state: ReturnType<typeof useShelterReminderPage>;
  sentinelRef: (node: HTMLElement | null) => void;
  onSelect: (r: AnimalMedicalReminder) => void;
  onOpenAnimal: (animalId: string) => void;
}> = ({ state, sentinelRef, onSelect, onOpenAnimal }) => {
  const { reminders, loading, loadingMore, hasMore, error, reload } = state;

  if (loading) {
    return (
      <div className="animate-pulse space-y-2">
        {[0, 1, 2, 3].map(i => <div key={i} className="h-12 bg-gray-100 rounded-lg" />)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-10 text-center">
        <p className="text-gray-500 mb-3">Az emlékeztetők betöltése nem sikerült.</p>
        <button onClick={reload} className="text-sm font-semibold text-teal-700 hover:underline">Újrapróbálom</button>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-500">
            <tr>
              <th className="px-4 py-3">Állat</th>
              <th className="px-4 py-3">Típus</th>
              <th className="px-4 py-3">Megnevezés</th>
              <th className="px-4 py-3">Utolsó alkalom</th>
              <th className="px-4 py-3">Esedékesség</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {reminders.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                Nincs a szűrésnek megfelelő emlékeztető.
              </td></tr>
            )}
            {reminders.map(r => {
              const d = daysUntil(r.nextDueDate!);
              const overdue = d < 0;
              const dueSoon = !overdue && d <= MEDICAL_DUE_SOON_DAYS;
              return (
                <tr
                  key={r.id}
                  onClick={() => onSelect(r)}
                  className={`cursor-pointer hover:bg-gray-50 ${overdue ? 'bg-red-50/30' : dueSoon ? 'bg-amber-50/30' : ''}`}
                >
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={e => { e.stopPropagation(); if (r.animal) onOpenAnimal(r.animal.id); }}
                      className="text-left hover:text-teal-700 transition-colors"
                    >
                      <div className="font-bold text-gray-900">{r.animal?.name ?? '—'}</div>
                      <div className="text-xs text-gray-500">{r.animal?.species}</div>
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-600">
                      {MEDICAL_EVENT_LABELS[r.eventType]}
                    </span>
                  </td>
                  <td className="px-4 py-3">{r.title}</td>
                  <td className="px-4 py-3 text-gray-500">{r.eventDate}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 font-semibold ${overdue ? 'text-red-700' : dueSoon ? 'text-amber-700' : 'text-gray-700'}`}>
                      {(overdue || dueSoon) && <AlertTriangle className="w-3.5 h-3.5" />}
                      {r.nextDueDate}
                      <span className="font-normal text-xs text-gray-400">
                        ({overdue ? `${Math.abs(d)} napja lejárt` : d === 0 ? 'ma' : `${d} nap múlva`})
                      </span>
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="py-5 flex items-center justify-center text-sm text-gray-400 gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> További emlékeztetők…
        </div>
      )}
      {!hasMore && reminders.length > 0 && (
        <div className="py-3 text-center text-xs text-gray-400">
          A lista végére értél — {reminders.length} emlékeztető.
        </div>
      )}
      {loadingMore && !hasMore && (
        <div className="py-3 flex justify-center"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>
      )}
    </>
  );
};

// ─── Apró elemek ────────────────────────────────────────────────────────────

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
}> = ({ active, onClick, icon, children }) => (
  <button
    type="button"
    role="tab"
    aria-selected={active}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${
      active ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'
    }`}
  >
    {icon}{children}
  </button>
);

const TONE_STYLES: Record<string, { text: string; icon: string }> = {
  neutral: { text: 'text-gray-700', icon: 'text-gray-400' },
  amber: { text: 'text-amber-700', icon: 'text-amber-500' },
  red: { text: 'text-red-700', icon: 'text-red-500' },
};

const SummaryChip: React.FC<{
  icon: React.ReactNode;
  label: string;
  value?: number;
  tone: keyof typeof TONE_STYLES;
  active?: boolean;
  onClick?: () => void;
  title?: string;
}> = ({ icon, label, value, tone, active, onClick, title }) => {
  const styles = TONE_STYLES[tone];
  const content = (
    <>
      <span className={styles.icon}>{icon}</span>
      <span className={`font-bold ${styles.text}`}>{value ?? '—'}</span>
      <span className="text-gray-500 text-xs">{label}</span>
    </>
  );

  if (!onClick) {
    return <span className="inline-flex items-center gap-1.5 px-2.5 py-1">{content}</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-colors ${
        active ? 'bg-red-100 ring-1 ring-red-200' : 'hover:bg-gray-50'
      }`}
    >
      {content}
    </button>
  );
};

export default MedicalRemindersPage;
